import { mapValues } from 'lodash';

import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  type SharingType,
} from '@/constants/data_shares';
import {
  type Partner,
  isManagedOfflinePartner,
  isOfflinePartner,
} from '@/types/partners';
import {
  ALL_SEGMENTS,
  OTHER,
  type PartnerPopulation,
  type Population,
  type SegmentType,
} from '@/types/populations';

import type {
  OutgoingPopulationSharingLevel,
  PopulationSharingLevel,
  SegmentSharingLevel,
  SharingPartnerRow,
} from './types';

/** Higher number = higher sharing level. */
const VISIBILITY_RANK: Record<SharingType, number> = {
  [HIDDEN]: 0,
  [OLAPS_ONLY]: 1,
  [SHARING]: 2,
  [GREENFIELD_SHARING]: 3,
};

function emptySegmentMap(): Record<SegmentType, SegmentSharingLevel> {
  return Object.fromEntries(
    ALL_SEGMENTS.map((segment) => [segment, null]),
  ) as Record<SegmentType, SegmentSharingLevel>;
}

/** A segment stays `null` only when it holds no populations at all. */
function sharingLevels<
  T extends {
    id: number;
    name: string;
    segment?: SegmentType | null;
    integration_type: string;
  },
>(
  items: T[],
  levelOf: (item: T) => SharingType,
): {
  bySegment: Record<SegmentType, SegmentSharingLevel>;
  byPopulation: Record<number, PopulationSharingLevel>;
} {
  const bySegment = emptySegmentMap();
  const byPopulation: Record<number, PopulationSharingLevel> = {};

  for (const item of items) {
    const { id, name } = item;
    const segment = item.segment ?? OTHER;
    const level = levelOf(item);

    byPopulation[id] = {
      id,
      name,
      segment,
      level,
      integrationType: item.integration_type,
    };

    const current = bySegment[segment];
    if (current == null || VISIBILITY_RANK[level] > VISIBILITY_RANK[current]) {
      bySegment[segment] = level;
    }
  }

  return { bySegment, byPopulation };
}

export function groupPartnerPopulationsByOrg(
  partnerPopulations: PartnerPopulation[],
): Map<number, PartnerPopulation[]> {
  const byOrg = new Map<number, PartnerPopulation[]>();
  for (const partnerPop of partnerPopulations) {
    const existing = byOrg.get(partnerPop.organization_id);
    if (existing) existing.push(partnerPop);
    else byOrg.set(partnerPop.organization_id, [partnerPop]);
  }
  return byOrg;
}

export type PartnerRowDependencies = {
  populations: Population[];
  partnerPopulations: PartnerPopulation[];
  /** Partner org id -> partner population ids shared as greenfield. */
  greenfieldSharesLookup: Record<number, number[] | undefined>;
  /** What we share with this partner for one of our populations. */
  getOutgoingVisibility: (
    populationId: number,
    partnerOrgId: number,
  ) => SharingType;
  /** Partner-population overrides we have for one of our populations. */
  getOutgoingOverrideCount: (
    populationId: number,
    partnerOrgId: number,
  ) => number;
  /** Number of fields the partner shares with us for one of their populations. */
  getIncomingSharedFieldCount: (partnerPopulationId: number) => number;
};

/**
 * Effective incoming visibility for one partner population. Mirrors
 * usePartnerSharing.getSharingInformation:
 *  - no incoming sharing rule   -> aggregates_only (counts)
 *  - rule with shared_fields    -> full or greenfield_full
 *  - rule without shared_fields -> aggregates_only
 */
function getIncomingVisibility(
  partnerPopulationId: number,
  sharedFieldCount: number,
  greenfieldPopIds: Set<number>,
): SharingType {
  if (!sharedFieldCount) return OLAPS_ONLY;
  return greenfieldPopIds.has(partnerPopulationId)
    ? GREENFIELD_SHARING
    : SHARING;
}

function buildOwner(partner: Partner): SharingPartnerRow['owner'] {
  const authorizer = partner.partnership_authorizer;
  if (!authorizer) return null;
  return {
    user_id: authorizer.id,
    name:
      [authorizer.first_name, authorizer.last_name].filter(Boolean).join(' ') ||
      authorizer.email,
  };
}

export function buildPartnerRows(
  partners: Partner[],
  {
    populations,
    partnerPopulations,
    greenfieldSharesLookup,
    getOutgoingVisibility,
    getOutgoingOverrideCount,
    getIncomingSharedFieldCount,
  }: PartnerRowDependencies,
): SharingPartnerRow[] {
  const partnerPopsByOrg = groupPartnerPopulationsByOrg(partnerPopulations);
  // Every row visits every population, so copy them out of their reactive
  // proxies once rather than once per partner.
  const ownPopulations = populations.map(
    ({ id, name, segment, integration_type }) => ({
      id,
      name,
      segment,
      integration_type,
    }),
  );

  return partners
    .filter(
      (partner) =>
        !isOfflinePartner(partner) && !isManagedOfflinePartner(partner),
    )
    .map((partner) => {
      const greenfieldPopIds = new Set(
        greenfieldSharesLookup[partner.id] ?? [],
      );

      const incoming = sharingLevels(
        partnerPopsByOrg.get(partner.id) ?? [],
        (partnerPop) =>
          getIncomingVisibility(
            partnerPop.id,
            getIncomingSharedFieldCount(partnerPop.id),
            greenfieldPopIds,
          ),
      );

      const outgoing = sharingLevels(ownPopulations, (population) =>
        getOutgoingVisibility(population.id, partner.id),
      );

      const outgoingPopulations = mapValues(
        outgoing.byPopulation,
        (entry): OutgoingPopulationSharingLevel => ({
          ...entry,
          overridesCount: getOutgoingOverrideCount(entry.id, partner.id),
        }),
      );

      return {
        id: partner.id,
        uuid: partner.uuid,
        partner: {
          name: partner.name,
          domain: partner.clearbit_domain ?? null,
          logo_url: partner.logo_url ?? null,
        },
        incoming: incoming.bySegment,
        outgoing: outgoing.bySegment,
        incomingPopulations: incoming.byPopulation,
        outgoingPopulations,
        owner: buildOwner(partner),
        tags: (partner.tags ?? []).map((tag) => ({
          // The OAS types tag.id as unknown, so coercing to a number yields NaN
          // for the non-numeric ids the API actually returns, which then
          // collide as :key values.
          id: String(tag.id),
          label: tag.label,
        })),
        lastActivity: null,
        lastSync: null,
      };
    });
}
